import { useEffect, useState } from "react";
import { getBasicAuthHeader } from "../../Utils/getBasicAuthHeader";

// Each stage (guide/language/version) gets its own AbortController so that
// starting a new request only cancels a previous request of the same stage.
// When an upstream stage changes (e.g. guide), downstream stages (language,
// version) are also aborted since their in-flight requests are now obsolete.
const controllers = {
    guide: null,
    language: null,
    version: null,
};

async function fetchData(endpoint, stage) {
    const stages = Object.keys(controllers);
    const stageIndex = stages.indexOf(stage);

    // Starting a new request for this stage invalidates its own in-flight
    // request as well as any downstream stage requests that depended on the
    // data this stage is about to replace (e.g. a new guide request aborts
    // stale language/version requests tied to the previous guide).
    stages.slice(stageIndex).forEach((affectedStage) => {
        if (controllers[affectedStage]) {
            controllers[affectedStage].abort();
            controllers[affectedStage] = null;
        }
    });

    controllers[stage] = new AbortController();
    const authorization = getBasicAuthHeader();

    const settings = {
        method: 'GET',
        credentials: window.includeApiCredentials ? 'include' : 'same-origin',
        signal: controllers[stage].signal,
        headers: authorization ? {
            'Authorization': authorization,
        } : {}
    }

    const response = await fetch(endpoint, settings);

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
}

export const useAudioguide = (defaultLanguage, onOpen, onExpand) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [source, setSource] = useState();
    const [languageCode, setLanguageCode] = useState();
    const [versionSlug, setVersionSlug] = useState();
    const [requestedTrack, setRequestedTrack] = useState(null);

    const [guide, setGuide] = useState(null);
    const [language, setLanguage] = useState(null);
    const [version, setVersion] = useState(null);
    
    const currentLanguage = languageCode || defaultLanguage;

    useEffect(() => {
        const openAudioguideListener = (event) => {
            const eventGuide = event.detail?.guide || null;
            const eventLanguage = event.detail?.language || null;
            const eventVersion = event.detail?.version || null;
            const eventTrack = event.detail?.track || null;
            const eventExpand = event.detail?.expand || false;

            if (eventGuide && eventGuide !== source) {
                setSource(eventGuide);
            }

            if (eventLanguage && eventLanguage !== language) {
                setLanguageCode(eventLanguage);
            }

            if (eventVersion && eventVersion !== version) {
                setVersionSlug(eventVersion);
            }

            if (eventTrack) {
                setRequestedTrack(eventTrack);
            }

            if (eventExpand) {
                onExpand();
            }

            onOpen();
        };

        window.addEventListener("audioguide:open", openAudioguideListener);

        return () => {
            window.removeEventListener("audioguide:open", openAudioguideListener);
        };
    }, []);

    useEffect(() => {
        if (guide) {
            setGuide(null);
            setLanguage(null);
            setVersion(null);
        }

        if (!source) {
            return;
        }

        const fetchGuide = async () => {
            setLoading(true);

            try {
                const guideData = await fetchData(source, 'guide');
                setGuide(guideData);
                setError(null);
            } catch (error) {
                // Ignore AbortError when a rapid source change supersedes this request
                // (same race condition as fetchLanguage/fetchVersion below).
                if (error.name !== 'AbortError') {
                    setError(error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchGuide();
    }, [source]);

    useEffect(() => {
        if (!guide) {
            return;
        }

        if (language && language.code === currentLanguage) {
            return;
        }

        if (language) {
            setVersion(null);
            setLanguage(null);
        }

        const language = guide.languages.find((lang) => (lang.code === currentLanguage)) ?? guide.languages[0];

        const fetchLanguage = async () => {
            setLoading(true);

            try {
                const langData = await fetchData(language.source, 'language');
                setLanguage(langData);
                setLanguageCode(language.code);
                // Clear any prior error once this stage succeeds (mirroring fetchGuide),
                // so a previous failure doesn't linger after recovery.
                setError(null);
            } catch (error) {
                // Ignore AbortError when this stage's request is superseded by a newer
                // selection. That abort is expected cancellation, not a real failure,
                // so it must not be surfaced as an error.
                if (error.name !== 'AbortError') {
                    setError(error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchLanguage();
    }, [guide, currentLanguage]);

    useEffect(() => {
        if (!language) {
            return;
        }

        if (version && version.slug === versionSlug) {
            return;
        }

        const version = language.versions.find((version) => (version.slug === versionSlug)) ?? language.versions[0];

        const fetchVersion = async () => {
            setLoading(true);

            try {
                const versionData = await fetchData(version.source, 'version');
                setVersion(versionData);
                setVersionSlug(version.slug);
                // Clear any prior error once this stage succeeds.
                setError(null);
                window.audioplayer.load(versionData.tracks);
            } catch (error) {
                // Same as fetchLanguage: ignore AbortError when this stage's request is
                // superseded by a newer version selection.
                if (error.name !== 'AbortError') {
                    setError(error);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchVersion();
    }, [language, versionSlug]);

    if (requestedTrack && version) {
        window.audioplayer.find(requestedTrack);
        setRequestedTrack(null);
    }

    const hasLoaded = guide && language && version;
    const isLoading = loading || (!hasLoaded && !error);

    return {
        loading: isLoading,
        error,
        guide,
        language,
        version,
        setSource: (value) => {
            if (value !== source) {
                window.audioplayer.reset();
                setSource(value);
                setGuide(null);
                setLanguage(null);
                setVersion(null);
            }
        },
        setLanguageCode: (value) => {
            if (value !== languageCode) {
                window.audioplayer.reset();
                setLanguageCode(value);
                setLanguage(null);
                setVersion(null);
            }
        },
        setVersionSlug: (value) => {
            if (value !== versionSlug) {
                window.audioplayer.reset();
                setVersionSlug(value);
                setVersion(null);
            }
        },
    };
}

